import { createRoute, redirect } from '@tanstack/react-router'
import { rootRoute } from './__root'
import { WorkspaceHeader } from '@/components/WorkspaceHeader'
import { apiClient } from '@/lib/api'
import { usePlatform } from '@/hooks/usePlatform'
import { useMemo } from 'react'
import {
  usePluginEntitlement,
  usePluginEntitlementMap,
} from '@/hooks/usePluginEntitlement'
import {
  listPluginsByCategory,
  type PluginCatalogEntry,
  type PluginCategory,
} from '@busswerks/shared'

/**
 * Plugin marketplace. Renders the full first-party module
 * catalog grouped by category. Each card shows pricing for
 * Personal + Commercial tiers and the user's current
 * entitlement state.
 *
 * Wave 7 scaffolding: buy buttons are CTA stubs that link into
 * the follow-up checkout flow. Entitlement resolution uses the
 * legacy tier bridge (`pro` / `studio` → all paid modules at
 * Personal level) until the per-module Stripe catalog + webhook
 * writes land.
 */
export const werkshopStoreRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/werkshop/store',
  beforeLoad: async () => {
    try {
      await apiClient.get('/api/v1/auth/me')
    } catch {
      throw redirect({ to: '/login' })
    }
  },
  component: WerkshopStore,
})

const CATEGORY_ORDER: ReadonlyArray<{
  key: PluginCategory
  label: string
}> = [
  { key: 'core', label: 'Core (free forever)' },
  { key: 'instrument-synth', label: 'Synths' },
  { key: 'instrument-sampler', label: 'Samplers' },
  { key: 'instrument-hybrid', label: 'Hybrid instruments' },
  { key: 'tool', label: 'Tools' },
  { key: 'effect-time', label: 'Time effects' },
  { key: 'effect-modulation', label: 'Modulation effects' },
  { key: 'effect-dynamics', label: 'Dynamics' },
  { key: 'effect-eq', label: 'EQ' },
  { key: 'effect-distortion', label: 'Distortion' },
  { key: 'effect-utility', label: 'Utility' },
]

function WerkshopStore() {
  const platform = usePlatform()
  const groups = listPluginsByCategory()
  const entitlementFor = usePluginEntitlementMap()

  // Sort within each category: owned entries surface first so
  // users see what they already have before browsing locked
  // modules. Ties fall back to the catalog's declaration order.
  const sortedGroups = useMemo(() => {
    const weight = (p: PluginCatalogEntry) => {
      const ent = entitlementFor(p.id)
      if (ent === 'free') return 0
      if (ent === 'commercial') return 1
      if (ent === 'personal') return 2
      return 3 // locked last
    }
    const out: Record<PluginCategory, PluginCatalogEntry[]> = {
      core: [],
      'instrument-synth': [],
      'instrument-sampler': [],
      'instrument-hybrid': [],
      'effect-time': [],
      'effect-modulation': [],
      'effect-dynamics': [],
      'effect-eq': [],
      'effect-distortion': [],
      'effect-utility': [],
      tool: [],
    }
    for (const [key, arr] of Object.entries(groups) as Array<
      [PluginCategory, PluginCatalogEntry[]]
    >) {
      out[key] = [...arr].sort((a, b) => weight(a) - weight(b))
    }
    return out
  }, [groups, entitlementFor])

  return (
    <div className="min-h-[100dvh] bg-surface-base">
      <WorkspaceHeader title="Store" workspaceSwitcher="werkshop" />
      <main
        className={
          platform.isNative
            ? 'w-full px-3 sm:px-4 py-4 flex flex-col gap-6'
            : 'max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex flex-col gap-8'
        }
      >
        <header className="flex flex-col gap-2">
          <h1 className="text-xl font-semibold text-content-primary">
            Werkshop Modules
          </h1>
          <p className="text-sm text-content-secondary max-w-2xl">
            Per-module licensing — Sequencer and MIDI Router are free forever.
            Every other module is available at a Personal or Commercial tier,
            one-time or subscription. Native AUv3 (iOS) and VST3 / AU (macOS)
            plugin hosting is on the way for the mobile + desktop apps.
          </p>
        </header>

        {CATEGORY_ORDER.map(({ key, label }) => {
          const plugins = sortedGroups[key] ?? []
          if (plugins.length === 0) return null
          return (
            <section key={key} className="flex flex-col gap-3">
              <h2 className="text-xs font-mono uppercase tracking-[0.14em] text-content-tertiary">
                {label}
              </h2>
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
                {plugins.map(p => (
                  <PluginCard key={p.id} plugin={p} />
                ))}
              </div>
            </section>
          )
        })}
      </main>
    </div>
  )
}

function PluginCard({ plugin }: { plugin: PluginCatalogEntry }) {
  const entitlement = usePluginEntitlement(plugin.id)
  const id = plugin.id
  const owned = entitlement !== null
  return (
    <article
      id={id}
      className="rounded-xl border border-brd-subtle bg-surface-raised p-4 flex flex-col gap-3"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-content-primary">
            {plugin.label}
          </h3>
          <p className="text-xs text-content-secondary">{plugin.tagline}</p>
        </div>
        {owned ? (
          <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-accent/20 text-accent">
            {entitlement === 'free'
              ? 'Free'
              : entitlement === 'personal'
                ? 'Personal'
                : 'Commercial'}
          </span>
        ) : (
          <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border border-brd-subtle text-content-tertiary">
            Locked
          </span>
        )}
      </header>

      <p className="text-xs text-content-secondary leading-relaxed">
        {plugin.description}
      </p>

      {plugin.free ? (
        <p className="text-xs text-accent">
          Always included. No purchase required.
        </p>
      ) : plugin.prices ? (
        <div className="flex flex-col gap-2 text-xs border-t border-brd-subtle pt-3">
          <PriceRow label="Personal" prices={plugin.prices.personal} />
          <PriceRow label="Commercial" prices={plugin.prices.commercial} />
          {!owned && (
            <p className="text-[11px] text-content-tertiary italic mt-1">
              Checkout is on the way — catalog preview available today; the buy
              buttons light up as soon as payments are wired.
            </p>
          )}
        </div>
      ) : (
        <p className="text-xs text-content-tertiary">Pricing coming soon.</p>
      )}
    </article>
  )
}

function PriceRow({
  label,
  prices,
}: {
  label: string
  prices: { monthly?: number; annual?: number; once?: number }
}) {
  const parts: string[] = []
  if (typeof prices.once === 'number') parts.push(`$${prices.once} once`)
  if (typeof prices.monthly === 'number') parts.push(`$${prices.monthly}/mo`)
  if (typeof prices.annual === 'number') parts.push(`$${prices.annual}/yr`)
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-content-tertiary text-[11px] uppercase tracking-wider">
        {label}
      </span>
      <span className="font-mono text-content-primary">
        {parts.length > 0 ? parts.join(' · ') : '—'}
      </span>
    </div>
  )
}
